import {
  OperandTypes,
  SymbolTable,
} from 'app/modules/rulesAdvanced/lib/SymbolTable';
import { IRSchema, ValueTypes } from 'app/modules/rulesAdvanced/models';
import { Unit21DataClassifier } from 'app/modules/dataSettings/responses';
import {
  DmbQueryBuilderOptionCode,
  Prefix,
} from 'app/modules/detectionModels/dmbTypes';

const TRM_FIELDS = [
  { key: 'trm_address', label: 'TRM / Address', datatype: 'string' },
  { key: 'trm_chain', label: 'TRM / Chain', datatype: 'string' },
  {
    key: 'trm_address_highest_risk_score_level_label',
    label: 'TRM / Highest risk level',
    datatype: 'string',
  },
  {
    key: 'trm_address_highest_risk_score_level',
    label: 'TRM / Highest risk score level',
    datatype: 'number',
  },
  {
    key: 'trm_risk_score_level',
    label: 'TRM / Risk score level',
    datatype: 'number',
  },
  {
    key: 'trm_address_incoming_volume_usd',
    label: 'TRM / Incoming volume USD',
    datatype: 'number',
  },
  {
    key: 'trm_address_outgoing_volume_usd',
    label: 'TRM / Outgoing volume USD',
    datatype: 'number',
  },
  {
    key: 'trm_address_total_volume_usd',
    label: 'TRM / Total volume USD',
    datatype: 'number',
  },
  {
    key: 'trm_address_incoming_transaction_count',
    label: 'TRM / Incoming transaction count',
    datatype: 'number',
  },
  {
    key: 'trm_address_outgoing_transaction_count',
    label: 'TRM / Outgoing transaction count',
    datatype: 'number',
  },
  {
    key: 'trm_address_total_transaction_count',
    label: 'TRM / Total transaction count',
    datatype: 'number',
  },
] as const;

const TRM_RISK_INDICATOR_TYPES = [
  { key: 'trm_ownership_risk_indicators', label: 'Ownership' },
  { key: 'trm_counterparty_risk_indicators', label: 'Counterparty' },
  { key: 'trm_indirect_risk_indicators', label: 'Indirect' },
] as const;

const TRM_RISK_INDICATOR_CATEGORIES = [
  'Community Complaint',
  'Sanctions',
  'Banned or Controlled Substances',
  'Darknet Market',
  'High-Risk Exchange',
  'P2P Crypto Marketplace',
  'Gambling Service',
  'Mining',
  'Mixer',
  'Investment Fraud',
  'Wallet Cluster',
  'Cross-chain Swap',
  'Layering',
] as const;

const TRM_RISK_INDICATOR_SUBFIELDS = [
  {
    key: 'category_risk_score_level_label',
    label: 'Risk Level',
    datatype: 'string',
  },
  {
    key: 'total_volume_usd',
    label: 'Total Volume USD',
    datatype: 'number',
  },
  {
    key: 'total_instance_count',
    label: 'Total Instance Count',
    datatype: 'number',
  },
  {
    key: 'total_volume_percent',
    label: 'Total Volume Percent',
    datatype: 'number',
  },
] as const;

const buildRiskIndicatorFields = (
  prefix: string | undefined,
  prefixLabel: string | undefined,
  model: DmbQueryBuilderOptionCode,
  buildEntry: (
    fieldPath: string,
    fieldLabel: string,
    datatype: string,
    model: DmbQueryBuilderOptionCode,
  ) => SymbolTable[string],
): SymbolTable => {
  const result: SymbolTable = {};
  TRM_RISK_INDICATOR_TYPES.forEach(({ key: typeKey, label: typeLabel }) => {
    TRM_RISK_INDICATOR_CATEGORIES.forEach((category) => {
      TRM_RISK_INDICATOR_SUBFIELDS.forEach(
        ({ key: subKey, label: subLabel, datatype }) => {
          const fieldPath = prefix
            ? `${prefix}.${typeKey}.${category}.${subKey}`
            : `${typeKey}.${category}.${subKey}`;
          const fieldLabel = prefixLabel
            ? `${prefixLabel} / TRM / Risk Indicators / ${typeLabel} / ${category} / ${subLabel}`
            : `TRM / Risk Indicators / ${typeLabel} / ${category} / ${subLabel}`;
          result[fieldPath] = buildEntry(
            fieldPath,
            fieldLabel,
            datatype,
            model,
          );
        },
      );
    });
  });
  return result;
};

export const buildTrmFieldsForAdvancedBuilder = (
  prefix?: Prefix,
  model: DmbQueryBuilderOptionCode = 'txn_instrument',
): SymbolTable => {
  const result: SymbolTable = {};
  TRM_FIELDS.forEach(({ key, label, datatype }) => {
    const fieldPath = prefix ? `${prefix.key}.${key}` : key;
    const fieldLabel = prefix ? `${prefix.label} / ${label}` : label;
    result[fieldPath] = {
      type: ValueTypes.FIELD,
      field: fieldPath,
      label: fieldLabel,
      model,
      datatype,
      cardinality: 'many',
    };
  });
  return {
    ...result,
    ...buildRiskIndicatorFields(
      prefix?.key,
      prefix?.label,
      model,
      (fieldPath, fieldLabel, datatype, m) => ({
        type: ValueTypes.FIELD,
        field: fieldPath,
        label: fieldLabel,
        model: m,
        datatype,
        cardinality: 'many',
      }),
    ),
  };
};

export const buildTrmFieldsForDropdownBuilder = (
  unit21DataClassifier: Unit21DataClassifier,
  prefix?: Prefix,
  model: DmbQueryBuilderOptionCode = 'txn_instrument',
): SymbolTable => {
  const result: SymbolTable = {};
  TRM_FIELDS.forEach(({ key, label, datatype }) => {
    const fieldPath = prefix ? `${prefix.key}.${key}` : key;
    const fieldLabel = prefix ? `${prefix.label} / ${label}` : label;
    const operandType =
      datatype === 'number' ? OperandTypes.NUMBER : OperandTypes.TEXT;
    result[fieldPath] = {
      type: operandType,
      text: fieldLabel,
      value: {
        operandType,
        unit21DataClassifier,
        value: {
          type: ValueTypes.FIELD,
          field: fieldPath,
          label: fieldLabel,
          model,
          datatype,
          cardinality: 'many',
        },
      },
    };
  });
  return {
    ...result,
    ...buildRiskIndicatorFields(
      prefix?.key,
      prefix?.label,
      model,
      (fieldPath, fieldLabel, datatype, m) => {
        const operandType =
          datatype === 'number' ? OperandTypes.NUMBER : OperandTypes.TEXT;
        return {
          type: operandType,
          text: fieldLabel,
          value: {
            operandType,
            unit21DataClassifier,
            value: {
              type: ValueTypes.FIELD,
              field: fieldPath,
              label: fieldLabel,
              model: m,
              datatype,
              cardinality: 'many',
            },
          },
        };
      },
    ),
  };
};

export const TRM_ALERT_SUPPRESSION_TOOLTIP =
  "Rule will not duplicate rule alerts for the same wallet if there haven't been any changes to the risky categories";

const TRM_FIELD_KEYS: Set<string> = new Set([
  ...TRM_FIELDS.map(({ key }) => key),
  ...TRM_RISK_INDICATOR_TYPES.map(({ key }) => key),
]);

const isRecord = (node: unknown): node is Record<string, unknown> =>
  node != null && typeof node === 'object' && !Array.isArray(node);

const containsTrmField = (node: unknown): boolean => {
  if (Array.isArray(node)) {
    return node.some(containsTrmField);
  }
  if (!isRecord(node)) {
    return false;
  }
  if ('type' in node && 'field' in node) {
    const { field } = node;
    if (typeof field === 'string') {
      const baseKey = field.includes('.')
        ? field.split('.').find((seg) => TRM_FIELD_KEYS.has(seg))
        : field;
      if (baseKey && TRM_FIELD_KEYS.has(baseKey)) {
        return true;
      }
    }
  }
  return Object.values(node).some(containsTrmField);
};

export const hasTrmFields = (spec: IRSchema): boolean => containsTrmField(spec);
